<template>
  <main class="main main--has-floating-buttons grid--dense grid--min-height">
    <spinner-overlay v-if="sendInProgress" />
    <portal to="headerTitle">
      {{ $t(`email.title.${$route.meta.parent}`, [id2number(parentId)]) }}
    </portal>
    <card :title="$t('pageEmail.card.email')" cols="12 6@desktop">
      <hm-card-inner v-if="$route.params.attachment === 'LATEST' && (isEmpty(files) || isEmpty(attachmentId))" :full-size="true">
        <hm-col cols="12">
          <span class="warning-text warning-text--full-size warning-text--no-margin col-12">{{ $t('pageEmail.noAttachmentWarning') }}</span>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner>
        <hm-select id="email"
                   v-model="email"
                   :options="emails"
                   :taggable="true"
                   :parent-errors="errors"
                   :label="$t('pageEmail.email')"
                   index="email"
                   label-key="email"
                   value-expression="email"
                   label-expression="${option.email}"
                   name="email"
                   data-vv-as="E-Mail"
                   @blur="$v.email.$touch()">
          <template slot="option" slot-scope="props">
            <table>
              <tr>
                <td v-html="props.option.email || props.option.name"></td>
              </tr>
            </table>
          </template>
        </hm-select>
        <hm-input-select id="template"
                         v-model="templateId"
                         :parent-errors="errors"
                         :label="$t('pageEmail.template')"
                         name="templateId"
                         cols="12"
                         @blur="$v.templateId.$touch()">
          <option v-for="template in templates" :value="template.id">{{ template.name }}</option>
        </hm-input-select>
        <hm-input-select v-if="!isEmpty(files)"
                         v-model="attachmentId"
                         :required="false"
                         :label="$t('pageEmail.attachment')"
                         name="attachment"
                         cols="12">
          <option :value="null"></option>
          <option v-for="file in files" :value="file.id">{{ file.fileName }} // {{ file.creationDate | toLocalDateTime }}</option>
        </hm-input-select>
        <hm-input v-model="emailSubject"
                  :label="$t('pageEmail.emailSubject')"
                  :parent-errors="errors"
                  name="emailSubject"
                  cols="12"
                  @blur="$v.emailSubject.$touch()" />
      </hm-card-inner>
    </card>
    <tabable-card id="content-data" :title="$t('pageEmail.card.preview')" cols="12 6@desktop" rows="3">
      <tabable-card-content :center="true" :title="$t('pageEmail.card.preview')">
        <hm-card-inner :full-size="true">
          <email-editor v-if="!isEmpty(emailContent)" ref="emailEditor" :srcdoc="emailContent" :key="emailContent.hashCode()" />
          <hint v-else :text="$t('pageEmail.hintSelectTemplate')" />
        </hm-card-inner>
      </tabable-card-content>
      <tabable-card-content v-if="hasRole('ROLE_SYSADMIN')" :title="$t('pageEmail.card.rawData')">
        <hm-card-inner :full-size="true">
          <pre v-html="jsonData"></pre>
        </hm-card-inner>
      </tabable-card-content>
    </tabable-card>

    <hm-floating-buttons ref="floatingButtons">
      <hm-floating-button :disabled="$v.$anyError || isEmpty(templateId) || isEmpty(emailSubject)"
                          :label="$t('pageEmail.actions.sendEmail')"
                          icon="send-email"
                          color="mint"
                          @click="sendEmail" />
    </hm-floating-buttons>
  </main>
</template>


<script>
/* eslint func-names: 0 */

import { isEmpty, isNil, last, isFunction, sortBy, head } from 'lodash';
import uuid from 'uuid';
import HotelmeisterMixin from '../../mixins/HotelmeisterMixin';
import EmailEditor from '../lib/util/EmailEditor';
import HmCardInner from '../lib/layout/HmCardInner';
import HmSelect from '../lib/inputs/HmSelect';
import TabableCard from '../lib/layout/TabableCard';
import TabableCardContent from '../lib/layout/TabableCardContent';
import ValidateMixin from '../../mixins/ValidateMixin';
import TimerMixin from '../../mixins/TimerMixin';

export default {
  name: 'PageEmail',
  components: { TabableCardContent, TabableCard, HmSelect, HmCardInner, EmailEditor },
  mixins: [HotelmeisterMixin, ValidateMixin, TimerMixin],

  data() {
    return {
      sendInProgress: false,
      email: null,
      templateId: null,
      attachmentId: null,
      emailSubject: null,
      emailContent: null,
      renderParams: {
        confirmationCode: this.$route.params.confirmationNumberOrReservationGroupId,
        guestOfferId: this.$route.params.guestOfferId,
        inquiryId: this.$route.params.inquiryId,
        reservationIds: isNil(this.$route.params.reservationId) ? null : [this.$route.params.reservationId],
        guestProfileId: this.$route.params.guestProfileId,
        invoiceNumber: null,
        receiptIdentifier: {
          parentId: null,
          receiptId: null,
        },
        folioIdentifier: {
          parentId: null,
          folioId: null,
        },
      },
      jsonData: null,
      templates: [],
      emails: [],
      files: [],
      templateType: this.$route.params.templateType,
    };
  },

  validations() {
    return {
      email: this.$hv__createValidators('required|email', this.$t('pageEmail.email')),
      emailSubject: this.$hv__createValidators('required', this.$t('pageEmail.emailSubject')),
      templateId: this.$hv__createValidators('required', this.$t('pageEmail.template')),
    };
  },

  computed: {
    parentId: function () {
      return this.$route.params.reservationId
        || this.$route.params.guestProfileId
        || this.$route.params.guestOfferId
        || this.$route.params.inquiryId
        || this.$route.params.confirmationNumberOrReservationGroupId;
    },
    secondaryId: function () {
      return this.$route.params.folioId;
    },
  },

  watch: {
    templateId: function () {
      this.updateEmail();
    },
  },

  mounted: function () {
  },

  created: function () {
    this.updateTemplates();
    this.updateEmail();
    this.updateEmails();
    this.updateFiles();

    if (!isNil(this.$route.query) && !isEmpty(this.$route.query.attachment)) {
      this.attachmentId = this.$route.query.attachment;
    }

    if (this.$route.name === 'guestFolioEmail' || this.$route.name === 'reservationFolioEmail') {
      this.axios.get(this.serviceUrls.folio.get(this, this.parentId, this.$route.params.folioId))
        .then((response) => {
          const folio = response.data.data;

          this.renderParams.invoiceNumber = folio.invoiceNumber;
          this.renderParams.folioIdentifier.parentId = this.$route.params.reservationId || this.$route.params.guestProfileId;
          this.renderParams.folioIdentifier.folioId = folio.id;
          this.renderParams.receiptIdentifier.parentId = this.$route.params.reservationId || this.$route.params.guestProfileId;
          this.renderParams.receiptIdentifier.receiptId = isEmpty(folio.payments) ? null : folio.payments[0].receiptHolderId;
        });
    }

    if (!isEmpty(this.$route.params.confirmationNumberOrReservationGroupId)) {
      this.axios.get(this.serviceUrls.reservationGroup.get(this, this.$route.params.confirmationNumberOrReservationGroupId))
        .then((response) => {
          const reservationGroup = response.data.data;

          this.renderParams.reservationIds = reservationGroup.roomReservations.map(it => it.reservationId);
          this.renderParams.reservationIds = null;
        });
    }

    this.$serviceBus.$on('help.show', this.showHelp);
  },

  methods: {
    showHelp() {
      this.$driver.defineSteps([
        {
          element: '#email',
          padding: 5,
          popover: {
            title: 'E-Mail-Adresse',
            description: 'Sie können entweder eine gespeicherte E-Mail-Adresse auswählen oder eine E-Mail-Adresse eingeben. Wenn nur eine E-Mail-Adresse vorhanden ist, so wird diese vorausgewählt.',
            position: 'bottom-center',
          },
        },
        {
          element: '#template',
          padding: 5,
          popover: {
            title: 'Vorlage',
            description: 'Die E-Mail-Funktion ist kontextbezogen - d.h. um eine Reservierungsbestätigung zu senden musst du die E-Mail-Funktion über der Reservierung öffnen, um eine Zahlungsbestägigung zu senden über das Folio mit der Zahlung, um ein Angebot zu senden über das Angebot, usw.',
            position: 'bottom-center',
          },
        },
      ]);
      this.$driver.start();
    },
    sendEmail() {
      if (isEmpty(this.email)) {
        this.showErrorMessage(this.$t('pageEmail.errorRecipientMustNotBeEmpty'));
        return;
      }
      this.sendInProgress = true;
      this.axios.post(this.serviceUrls[this.$route.meta.parentServiceUrl].email(this, this.parentId, this.secondaryId), {
        id: uuid.v4(),
        recipient: this.email,
        subject: this.emailSubject,
        content: this.$refs.emailEditor.getContent(),
        attachmentId: this.attachmentId,
        templateId: this.templateId,
      })
        .then((response) => {
          this.showSuccessMessage();
          if (!isEmpty(this.$route.meta.parent)) {
            this.$router.push({
              name: this.$route.meta.parent,
              params: this.$route.params,
            });
          }
        })
        .catch((error) => {
          this.showMessages(error.response);
        })
        .finally(() => {
          this.sendInProgress = false;
        });
    },
    id2number: function (value) {
      if (isEmpty(value)) return '';
      return last(value.split('::'));
    },
    updateFiles() {
      if (isFunction(this.serviceUrls[this.$route.meta.parentServiceUrl].correspondence)) {
        this.axios.get(this.serviceUrls[this.$route.meta.parentServiceUrl].correspondence(this, this.parentId, this.secondaryId))
          .then((response) => {
            this.files = response.data.data.filter(it => it.metadata.MEDIA_TYPE !== 'Email'
              && (it.metadata.DOCUMENT_TYPE === 'Invoice' || it.metadata.DOCUMENT_TYPE === 'Offer' || it.metadata.DOCUMENT_TYPE === 'Document'));

            this.files = sortBy(this.files, 'creationDate').reverse();

            if (!isEmpty(this.files) && this.$route.params.attachment === 'LATEST') {
              this.attachmentId = head(this.files).id;
            } else if (this.$route.params.attachment === 'LATEST') {
              this.setTimeout(this.updateFiles, 1000);
            }
          });
      } else {
        this.files = [];
      }
    },
    updateEmails() {
      this.axios.get(this.serviceUrls[this.$route.meta.parentServiceUrl].email(this, this.parentId, this.secondaryId), {
        params: {
          type: 'autocomplete',
        },
      })
        .then((response) => {
          this.emails = response.data.data.map(it => ({ email: it, id: it, name: it }));

          if (isEmpty(this.email) && !isEmpty(this.emails) && this.emails.length > 0) {
            this.$nextTick(() => {
              this.email = this.emails[0].email;
            });
          }
        });
    },
    updateTemplates() {
      this.axios.get(this.serviceUrls.templateHelper.filter(this, 'Email'))
        .then((response) => {
          let filteredTemplates = response.data.data;

          if (isEmpty(this.renderParams.confirmationCode) && isEmpty(this.renderParams.reservationIds)) {
            filteredTemplates = filteredTemplates.filter(it => it.type !== 'ReservationConfirmationEmail');
          }

          if (isEmpty(this.renderParams.guestOfferId)) {
            filteredTemplates = filteredTemplates.filter(it => it.type !== 'GuestOfferEmail');
          }

          if (!isEmpty(this.templateType)) {
            filteredTemplates = filteredTemplates.filter(it => it.type === this.templateType);
          }

          filteredTemplates = sortBy(filteredTemplates, ['name']);

          this.templates = filteredTemplates;

          if (filteredTemplates.length === 1) {
            this.templateId = filteredTemplates[0].id;
          }
        });
    },
    updateEmail() {
      if (isEmpty(this.templateId)) {
        this.emailContent = null;
        this.emailSubject = null;
        this.jsonData = null;
      } else {
        this.axios.post(this.serviceUrls.templateHelper.render(this, this.templateId), this.renderParams)
          .then((response) => {
            this.emailContent = response.data.data.content;
            this.emailSubject = response.data.data.title;
          })
          .catch((error) => {
            this.showMessages(error.response);
          });
        this.axios.post(this.serviceUrls.templateHelper.data(this), this.renderParams)
          .then((response) => {
            this.jsonData = response.data.data;
          })
          .catch((error) => {
            this.showMessages(error.response);
          });
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
  @import '~styles/import';
</style>



// WEBPACK FOOTER //
// src/components/pages/PageEmail.vue